// ZUSTAND→ATOM off-ramp (docs/projects/complete/web/zustand-to-effect-atom-migration.md)
// Status: converted
// atomKvsSync persisted atom; zustand `{state, version}` envelopes migrate
// forward through the schema transform below.
import { idleQuery } from '@listening/frontend/query/api-runtime';
import {
  userPreferencesAtom,
  userProfileAtom,
} from '@listening/frontend/query/user-atoms';
import { useAtomSet, useAtomValue } from '@effect/atom-react';
import { atomKvsSync } from '@listening/frontend/atoms/kvs';
import { Schema, SchemaTransformation, Struct } from 'effect';
import { AsyncResult } from 'effect/reactivity';
import { useMemo } from 'react';
import { authClient } from '@/lib/auth-client';
import { useAuthSession } from '@/services/auth-service';
import { isSessionAbsenceConfirmed } from '@/services/session-auth-state';

/**
 * Imperative session check — runs outside React (e.g. `beforeLoad`).
 */
export async function isUserLoggedIn() {
  const { data } = await authClient.getSession();
  return data?.user ? data.user.id : null;
}

/** Hook: is the user logged in? Returns truthy when authenticated. */
export const useIsUserLoggedIn = () => {
  const { user } = useAuthSession();
  return user ? user.userId : null;
};

/** Hook: session info (userId, email, isLoggedIn). */
export const useUserSessionInfo = () => {
  const { user, isPending } = useAuthSession();
  return {
    userId: user?.userId,
    email: user?.email,
    isLoggedIn: !!user,
    isPending,
  };
};

/** Hook: auth status for components. */
export const useUserToken = () => {
  const { user, isPending, hasError, errorStatus } = useAuthSession();
  return {
    isAuthorized: !!user,
    errorStatus,
    hasHydrated: !isPending,
    isSessionAbsenceConfirmed: isSessionAbsenceConfirmed({
      hasUser: !!user,
      isPending,
      hasError,
      errorStatus,
    }),
  };
};

// The shared preferences read, gated on the app's own session hook
// (auth-gating is app-specific).
export const useUserPreferences = () => {
  const userLoggedIn = useIsUserLoggedIn();
  return useAtomValue(userLoggedIn ? userPreferencesAtom : idleQuery);
};

export const useNameAndEmail = () => {
  const { user: sessionUser } = useAuthSession();
  const email = sessionUser?.email;
  const profileData = AsyncResult.getOrElse(
    useAtomValue(userProfileAtom),
    () => undefined,
  );

  const fullName =
    `${profileData?.first_name ?? ''} ${profileData?.last_name ?? ''}`.trim();
  const letters = useMemo(() => {
    if (fullName) return fullName.slice(0, 2).toUpperCase();
    if (email) return email.slice(0, 2).toUpperCase();
    return 'LI';
  }, [email, fullName]);
  return {
    email,
    fullName: fullName.length > 0 ? fullName : undefined,
    firstName: profileData?.first_name,
    lastName: profileData?.last_name,
    letters,
  };
};

const localUserPrefsStateSchema = Schema.Struct({
  homeScreenLayout: Schema.Literals(['grid', 'list']),
  sidebarCollapsed: Schema.Boolean,
  hiddenInCompleteConversions: Schema.Array(Schema.String),
  erroredAudioIds: Schema.Array(Schema.String),
});
type LocalUserPrefsValues = typeof localUserPrefsStateSchema.Type;

const defaultLocalUserPrefs: LocalUserPrefsValues = {
  homeScreenLayout: 'grid',
  sidebarCollapsed: false,
  hiddenInCompleteConversions: [],
  erroredAudioIds: [],
};

// zustand-persist wrote a `{state, version}` envelope (state possibly
// partial). Decode merges over defaults; encode writes the bare shape. See
// plan doc §C2.
const partialLocalUserPrefsSchema = localUserPrefsStateSchema.mapFields(
  Struct.map(Schema.optionalKey),
);
const localUserPrefsAtom = atomKvsSync({
  key: 'user-prefs-storage',
  schema: Schema.Union([
    Schema.Struct({
      state: partialLocalUserPrefsSchema,
      version: Schema.Int,
    }),
    partialLocalUserPrefsSchema,
  ]).pipe(
    Schema.decodeTo(
      localUserPrefsStateSchema,
      SchemaTransformation.transform({
        decode: (stored) => ({
          ...defaultLocalUserPrefs,
          ...('state' in stored ? stored.state : stored),
        }),
        encode: (state) => state,
      }),
    ),
  ),
  defaultValue: () => defaultLocalUserPrefs,
});

type LocalUserPrefsState = LocalUserPrefsValues & {
  setHomeScreenLayout: (layout: 'grid' | 'list') => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  hideInCompleteConversion: (audioId: string) => void;
  addErroredAudioId: (audioId: string) => void;
  reset: () => void;
};

export function useLocalUserPrefsStore<T>(
  selector: (state: LocalUserPrefsState) => T,
): T {
  const setState = useAtomSet(localUserPrefsAtom);
  const actions = useMemo(
    () => ({
      setHomeScreenLayout: (layout: 'grid' | 'list') => {
        setState((s) => ({ ...s, homeScreenLayout: layout }));
      },
      setSidebarCollapsed: (collapsed: boolean) => {
        setState((s) => ({ ...s, sidebarCollapsed: collapsed }));
      },
      hideInCompleteConversion: (audioId: string) => {
        setState((s) => ({
          ...s,
          hiddenInCompleteConversions: [
            ...s.hiddenInCompleteConversions,
            audioId,
          ],
        }));
      },
      addErroredAudioId: (audioId: string) => {
        const MAX_ERRORED_IDS = 300;
        setState((s) => ({
          ...s,
          erroredAudioIds: [
            ...s.erroredAudioIds.slice(-MAX_ERRORED_IDS + 1),
            audioId,
          ],
        }));
      },
      reset: () => {
        setState(defaultLocalUserPrefs);
      },
    }),
    [setState],
  );
  // Derived-atom routing: gated on the selected value (zustand parity).
  return useAtomValue(localUserPrefsAtom, (state) =>
    selector({ ...state, ...actions }),
  );
}
